import {
  createSlice,
  isRejectedWithValue,
  PayloadAction,
} from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import {
  encryptedStorage,
  EncryptedStorageKeys,
} from '@setup/encryptedStorage/encryptedStorage';
import { userSlice } from '@features/api/userSlice';
import {
  User,
  UserBasicTokenResponse,
  UserType,
} from '@features/api/response/User';

type AuthState = {
  isAuthenticated: boolean;
  isLoading: boolean;
  lastLogin: string;
  isEmailVerified: boolean | undefined;
  userType: UserType | undefined;
};

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    isAuthenticated: !!encryptedStorage.getItem(
      EncryptedStorageKeys.AccessToken
    ),
    isEmailVerified: undefined,
    isLoading: false,
  } as AuthState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (value: any) =>
          userSlice.endpoints.signIn.matchPending(value) ||
          userSlice.endpoints.signUp.matchPending(value) ||
          userSlice.endpoints.logout.matchPending(value),
        (state) => {
          state.isLoading = true;
        }
      )
      .addMatcher(
        (value: any) =>
          isRejectedWithValue(value) && value.payload.status === 401,
        (state) => {
          state.isAuthenticated = false;
        }
      )
      .addMatcher(
        (value: any) =>
          value.meta?.rejectedWithValue &&
          (userSlice.endpoints.signIn.matchRejected(value) ||
            userSlice.endpoints.user.matchRejected(value) ||
            userSlice.endpoints.signUp.matchRejected(value) ||
            userSlice.endpoints.logout.matchRejected(value)),
        (state) => {
          state.isLoading = false;
          state.isAuthenticated = false;
        }
      )
      .addMatcher(
        (value: any) => userSlice.endpoints.signUp.matchFulfilled(value),
        (state, action: PayloadAction<UserBasicTokenResponse>) => {
          state.isLoading = false;
          state.lastLogin = action.payload.lastLogin;
        }
      )
      .addMatcher(
        (value: any) => userSlice.endpoints.signIn.matchFulfilled(value),
        (state) => {
          state.isLoading = false;
        }
      )
      .addMatcher(
        (value: any) =>
          userSlice.endpoints.updatePassword.matchFulfilled(value) ||
          userSlice.endpoints.accessToken.matchFulfilled(value),
        (state) => {
          state.isLoading = false;
          state.isAuthenticated = false;
          state.isEmailVerified = undefined;
          state.userType = undefined;
        }
      )
      .addMatcher(
        (value: any) => userSlice.endpoints.logout.matchFulfilled(value),
        (state) => {
          state.isLoading = false;
          state.isAuthenticated = false;
          state.isEmailVerified = undefined;
          state.userType = undefined;

          encryptedStorage.removeItem(EncryptedStorageKeys.AccessToken);
        }
      )
      .addMatcher(
        userSlice.endpoints.user.matchFulfilled,
        (state, action: PayloadAction<User>) => {
          state.isAuthenticated = true;
          state.isEmailVerified = action.payload.emailVerified;
          state.userType = action.payload.userType;
        }
      );
  },
});

export default authSlice.reducer;
export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;
